/**
 * SG AGM with UFS — 변수 ↔ 문서 매핑 (단일 소스).
 *
 * 사용자 명세 기준:
 *  1. 회사 공통 — company_name/reg_no: 전 7문서 / registered_office_address: Consent, Notice
 *  2. AGM Schedule — agm_date: DR 제외 6문서 / agm_time·agm_venue: 5문서 (Sole Minutes 제외)
 *  3. Financial — financial_year_end_date: Attendance·Consent 제외 5문서
 *  4. Agenda flags — is_short_notice: DR, Minutes(Multiple) / has_*: DR, Notice, Minutes 3종
 *  5. Member flags — is_*_member: Attendance, Consent only
 *  6. Entities — member_name, member_representative_name 등 문서별 상이
 */

export type AgmUfsDocKind =
  | 'attendance'
  | 'consent'
  | 'directors_resolution'
  | 'minutes_multiple'
  | 'minutes_sole_corporate'
  | 'minutes_sole_individual'
  | 'notice';

export const ALL_DOC_KINDS: AgmUfsDocKind[] = [
  'attendance',
  'consent',
  'directors_resolution',
  'minutes_multiple',
  'minutes_sole_corporate',
  'minutes_sole_individual',
  'notice',
];

export function classifyAgmUfsDoc(templateName: string): AgmUfsDocKind {
  const n = (templateName || '').toLowerCase();
  if (/minutes.*sole\s*individual/i.test(n)) return 'minutes_sole_individual';
  if (/minutes.*sole\s*corporate/i.test(n)) return 'minutes_sole_corporate';
  if (/minutes.*multiple/i.test(n)) return 'minutes_multiple';
  if (/consent.*short\s*notice/i.test(n)) return 'consent';
  if (/directors?['_\s]*resolutions?/i.test(n)) return 'directors_resolution';
  if (/director/i.test(n) && /allotment/i.test(n)) return 'directors_resolution';
  if (/notice.*agm/i.test(n) || /agm.*notice/i.test(n)) return 'notice';
  if (/attendance/i.test(n)) return 'attendance';
  return 'notice';
}

/** 변수 키 → 사용 문서 kind */
export const VARIABLE_DOC_KINDS: Record<string, AgmUfsDocKind[]> = {
  company_name: ALL_DOC_KINDS,
  company_registration_no: ALL_DOC_KINDS,

  registered_office_address: ['consent', 'notice'],

  agm_date: [
    'attendance',
    'consent',
    'minutes_multiple',
    'minutes_sole_corporate',
    'minutes_sole_individual',
    'notice',
  ],
  agm_time: ['attendance', 'consent', 'directors_resolution', 'minutes_multiple', 'notice'],
  agm_venue: ['attendance', 'consent', 'directors_resolution', 'minutes_multiple', 'notice'],

  financial_year_end_date: [
    'directors_resolution',
    'minutes_multiple',
    'minutes_sole_corporate',
    'minutes_sole_individual',
    'notice',
  ],
  is_part_of_group: ['directors_resolution'],

  is_short_notice: ['directors_resolution', 'minutes_multiple'],
  has_directors_remuneration: [
    'directors_resolution',
    'notice',
    'minutes_multiple',
    'minutes_sole_corporate',
    'minutes_sole_individual',
  ],
  has_retiring_directors: [
    'directors_resolution',
    'notice',
    'minutes_multiple',
    'minutes_sole_corporate',
    'minutes_sole_individual',
  ],

  is_multiple_members: ['attendance', 'consent'],
  is_sole_corporate_member: ['attendance', 'consent'],
  is_sole_individual_member: ['attendance', 'consent'],

  'member.name': ['attendance'],
  member_name: ['attendance', 'consent', 'minutes_sole_corporate', 'minutes_sole_individual'],
  member_representative_name: ['attendance', 'consent', 'minutes_sole_corporate'],

  chairman_name: ['minutes_multiple'],

  director_count: ['directors_resolution'],
  'director_count == 1': ['directors_resolution'],
  'director_count == 2': ['directors_resolution'],
  specify_signers: ['directors_resolution'],
  'signer.name': ['directors_resolution'],
  'director.name': ['directors_resolution'],

  resolution_date: ['directors_resolution'],
  notice_date: ['notice'],
  consent_date: ['consent'],

  Signers: ALL_DOC_KINDS,
};

/** 문서 kind → 해당 문서에 들어가는 변수 목록 (문서 목록 펼침용) */
export const DOC_VARIABLES_BY_KIND: Record<AgmUfsDocKind, string[]> = {
  attendance: [
    'company_name',
    'company_registration_no',
    'agm_venue',
    'agm_date',
    'agm_time',
    'is_multiple_members',
    'member.name',
    'is_sole_corporate_member',
    'member_name',
    'member_representative_name',
    'is_sole_individual_member',
    'Signers',
  ],
  consent: [
    'company_name',
    'company_registration_no',
    'registered_office_address',
    'is_multiple_members',
    'member_representative_name',
    'member_name',
    'is_sole_corporate_member',
    'is_sole_individual_member',
    'agm_venue',
    'agm_date',
    'agm_time',
    'consent_date',
    'Signers',
  ],
  directors_resolution: [
    'company_name',
    'company_registration_no',
    'financial_year_end_date',
    'is_part_of_group',
    'director_count',
    'specify_signers',
    'signer.name',
    'agm_venue',
    'agm_time',
    'is_short_notice',
    'has_directors_remuneration',
    'has_retiring_directors',
    'director.name',
    'resolution_date',
    'Signers',
  ],
  minutes_multiple: [
    'company_name',
    'company_registration_no',
    'agm_date',
    'agm_time',
    'agm_venue',
    'chairman_name',
    'is_short_notice',
    'financial_year_end_date',
    'has_directors_remuneration',
    'has_retiring_directors',
    'Signers',
  ],
  minutes_sole_corporate: [
    'company_name',
    'company_registration_no',
    'member_representative_name',
    'member_name',
    'agm_date',
    'financial_year_end_date',
    'has_directors_remuneration',
    'has_retiring_directors',
    'Signers',
  ],
  minutes_sole_individual: [
    'company_name',
    'company_registration_no',
    'member_name',
    'agm_date',
    'financial_year_end_date',
    'has_directors_remuneration',
    'has_retiring_directors',
    'Signers',
  ],
  notice: [
    'company_name',
    'company_registration_no',
    'registered_office_address',
    'agm_venue',
    'agm_date',
    'agm_time',
    'financial_year_end_date',
    'has_retiring_directors',
    'has_directors_remuneration',
    'notice_date',
    'Signers',
  ],
};

export function docKindsForVariable(varKey: string): AgmUfsDocKind[] {
  return VARIABLE_DOC_KINDS[varKey] ?? [];
}

export function docNamesForVariable(
  varKey: string,
  docs: Array<{ name: string; kind: AgmUfsDocKind }>,
): string[] {
  const kinds = docKindsForVariable(varKey);
  return docs.filter((d) => kinds.includes(d.kind)).map((d) => d.name);
}

export function docNamesForKinds(
  kinds: AgmUfsDocKind[],
  docs: Array<{ name: string; kind: AgmUfsDocKind }>,
): string[] {
  return docs.filter((d) => kinds.includes(d.kind)).map((d) => d.name);
}
